import { PageType } from "../config/page.types";


/**
 * Includes all explicit application routes.
 */
export enum AppRoutes {
	// Common routes
	Home = "/",

	// Auth routes.
	SignIn = "/auth/sign-in",
	Account = "/auth/account",
	ResetPassword = "/auth/recover/complete",

	// Error routes.
	Unauthorized = "/error/unauthorized",
	Forbidden = "/error/forbidden",
	NotFound = "/error/not-found",

	// New routes (will be re-organized in future versions).
	PostSignInHome = "/home",
	DataCollection = "/data-collection",
	DataCollectionMonitor = "/data-collection/monitor",
	DataCollectionImport = "/data-collection/import",
	DataCollectionConfiguration = "/data-collection/configuration",
	DataCollectionConfigurationOperationalUnits = "/data-collection/configuration/operational-units",
	DataCollectionConfigurationOperationalUnitsAddUnit = "/data-collection/configuration/operational-units/add-unit",
	DataCollectionConfigurationOperationalUnitsDetail = "/data-collection/configuration/operational-units/:id",
	Results = "/results",
	ResultsOverview = "/results/overview",
	ResultsDetails = "/results/details",
	ResultsReport = "/results/report",
	ResultsDashboardInsights = "/results/dashboard-insights",
	ResultsDashboardInsightsDetail = "/results/dashboard-insights/:id",
	ResultsTransport = "/results/transport",
	ResultsTransportGreenrouter = "/results/transport/greenrouter",
	ResultsTransportGreenrouterDetail = "/results/transport/greenrouter/:id",
	ResultsTransportGreenrouterEntityDetail = "/results/entities/:id",
	ResultsTransportThirdParty = "/results/transport/third-party",
	ResultsTransportThirdPartyDetail = "/results/transport/third-party/:id",
	ResultsEntityGreenRouterDetail = "/results/entities/:entityId/transports/greenrouter/:transportId",
	ResultsOperationalUnits = "/results/operational-units",
	ResultsOperationalUnitsGreenRouter = "/results/operational-units/greenrouter",
	ResultOperationalUnitsGreerouterNode = "/results/operational-units/greenrouter/:id",
	ResultOperationalUnitsGreerouterNodeConsumptions = "/results/operational-units/greenrouter/:id/consumptions",
	ResultsOperationalUnitsThirdParty = "/results/operational-units/third-party",
	ResultsOperationalUnitsThirdPartyDetail = "/results/operational-units/third-party/:id",
	ResultsMap = "/results/map",
	WhatIf = "/what-if",
	WhatIfSimulation = "/what-if/simulation",
	WhatIfCarbonBudget = "/what-if/carbon-budget",
	WhatIfRootCause = "/what-if/root-cause",
	WhatIfRootCauseResults = "/what-if/root-cause/results",
	AdditionalServices = "/additional-services",
	AdditionalServicesCarbonSnap = "/additional-services/carbon-snap",
	AdditionalServicesCarbonSnapResults = "/additional-services/carbon-snap/results",
	AdditionalServicesDocumentation = "/additional-services/documentation",
	AdditionalServicesTourWizard = "/additional-services/tour-wizard",
	Settings = "/settings",
	SettingsAdmin = "/settings/admin",
	SettingsAuthApi = "/settings/auth-api",
	SettingsPersonalised = "/settings/personalised",
}

/**
 * TODO: check if still needed
 * Defines routes used to catch unwanted navigations and redirect users to appropriate
 * application routes; these values should be used only in App component.
 */
export enum CatchRedirectAppRoutes {
	Network = "/network",
}

/**
 * GreenRouter UI menu item keys, used to highlight the correct menu item.
 */
export enum AppMenuKey {
	// Defined, but not used to populate the side menu.
	SignIn = "sign in",
	ResetPassword = "reset password",

	// New keys, used to populate the side menu.
	PostSignInHome = "home",
	DataCollection = "data collection",
	DataCollectionMonitor = "monitor",
	DataCollectionImport = "import",
	DataCollectionConfiguration = "configuration",
	DataCollectionConfigurationOperationalUnits = "operative units directory",
	DataCollectionConfigurationOperationalUnitsAddUnit = "add operative unit",
	DataCollectionConfigurationOperationalUnitsDetail = "detail",
	Results = "results",
	ResultsOverview = "overview",
	ResultsDetails = "details",
	ResultsReport = "report",
	ResultsDashboardInsights = "dashboard and insights",
	ResultsDashboardInsightsDetail = "dashboard and insights detail",
	ResultsTransport = "transport",
	ResultsTransportGreenrouter = "greenrouter transport",
	ResultsTransportGreenrouterDetail = "greenrouter detail transport",
	ResultsTransportThirdParty = "third party transport",
	ResultsTransportThirdPartyDetail = "third party detail transport ",
	ResultsOperationalUnits = "operational units",
	ResultsOperationalUnitsGreenRouter = "greenrouter operational units",
	ResultOperationalUnitsGreerouterNode = "greenrouter detail operational units",
	ResultsOperationalUnitsThirdParty = "third party operational units",
	ResultsOperationalUnitsThirdPartyDetail = "third party detail operational units",
	ResultsMap = "maps",
	WhatIf = "what if",
	WhatIfSimulation = "simulation",
	WhatIfCarbonBudget = "carbon-budget",
	WhatIfRootCause = "root-cause",
	WhatIfRootCauseResults = "results",
	AdditionalServices = "additional services",
	AdditionalServicesCarbonSnap = "carbon snap",
	AdditionalServicesCarbonSnapResults = "carbon snap results",
	AdditionalServicesDocumentation = "documentation",
	AdditionalServicesTourWizard = "tour and wizard",
	Settings = "settings",
	SettingsAdmin = "admin",
	SettingsAdminRoles = "roles",
	SettingsAdminUsers = "users",
	SettingsAdminCompany = "company",
	SettingsAuthApi = "auth and api",
	SettingsPersonalised = "personalised",
	Account = "account",

	Theme = "theme",
	SignOut = "sign out",

	ArrowDown = "arrow down",
}

/**
 * AppMenuSectionItem defines an item of an AppMenuSection.
 */
export type AppMenuSectionItem = {
	/**
	 * The key used to highlight the menu item section if set on the current navigation stack.
	 */
	key: AppMenuKey;

	/**
	 * The item route.
	 */
	route: AppRoutes | string;

	pageType: PageType;
};

/**
 * AppMenuSection defines one section of the application (side) menu.
 */
export type AppMenuSection = {
	/**
	 * The key used to highlight the menu section if set on the current navigation stack.
	 */
	key: AppMenuKey;

	/**
	 * The base section route; if items is not empty, the menu will never use this route.
	 */
	route?: AppRoutes | string;

	/**
	 * The array of this application section sub-items.
	 */
	items: Array<AppMenuSectionItem>;

	/**
	 * The PageType associated with this menu entry.
	 * Used to reset breadcrumbs.
	 */
	pageType: PageType;
};

/**
 * parametrize accepts a parametric routes and a map of parameters and produces
 * a valid navigation URL.
 *
 * @param route  the input route
 * @param params the map defining params to be used to build the navigation URL
 * @returns      a string representing a valid URL that can be used to navigate in app
 */
export const parametrize = (route: AppRoutes, params: Object): string => {
	let out = `${route}`;
	const keys = Object.keys(params) as Array<keyof typeof params>;
	keys.forEach((k) => {
		out = out.replace(`:${k}`, `${params[k]}`);
	});
	return out;
};

/**
 * setRouteQuery accepts a routes and a map of parameters and returns the same route
 * by adding given query parameters and returning a valid navigation URL.
 *
 * @param route  the input route
 * @param params the map defining params to be used to build the navigation URL
 * @returns      a string representing a valid URL that can be used to navigate in app
 */
export const setRouteQuery = (route: AppRoutes | string, params: Object): string => {
	const keys = Object.keys(params) as Array<keyof typeof params>;
	if (keys.length <= 0) {
		return route;
	}

	const query: Array<string> = [];
	keys.forEach((k) => {
		query.push(`${k}=${params[k]}`);
	});
	return `${route}?${query}`;
};

/**
 * AppMenuSection defines a section of the application (side) menu.
 */

export const appMenuRoutes: AppMenuSection[] = [
	{
		key: AppMenuKey.PostSignInHome,
		route: AppRoutes.PostSignInHome,
		pageType: PageType.Home,
		items: [],
	},
	{
		key: AppMenuKey.DataCollection,
		route: AppRoutes.DataCollection,
		pageType: PageType.DataCollectionMonitor,
		items: [
			{
				key: AppMenuKey.DataCollectionMonitor,
				route: AppRoutes.DataCollectionMonitor,
				pageType: PageType.DataCollectionMonitor,
			},
			{
				key: AppMenuKey.DataCollectionImport,
				route: AppRoutes.DataCollectionImport,
				pageType: PageType.DataCollectionImport,
			},
			{
				key: AppMenuKey.DataCollectionConfiguration,
				route: AppRoutes.DataCollectionConfiguration,
				pageType: PageType.DataCollectionConfiguration,
			},
		],
	},
	{
		key: AppMenuKey.Results,
		route: AppRoutes.Results,
		pageType: PageType.ResultsOverview,
		items: [
			{
				key: AppMenuKey.ResultsOverview,
				route: AppRoutes.ResultsOverview,
				pageType: PageType.ResultsOverview,
			},
			{
				key: AppMenuKey.ResultsDetails,
				route: AppRoutes.ResultsDetails,
				pageType: PageType.ResultsDetails,
			},
			{
				key: AppMenuKey.ResultsReport,
				route: AppRoutes.ResultsReport,
				pageType: PageType.ResultsReport,
			},
			{
				key: AppMenuKey.ResultsDashboardInsights,
				route: AppRoutes.ResultsDashboardInsights,
				pageType: PageType.ResultsDashboardInsights,
			},
			{
				key: AppMenuKey.ResultsTransport,
				route: AppRoutes.ResultsTransport,
				pageType: PageType.ResultsTransport,
			},
			{
				key: AppMenuKey.ResultsOperationalUnits,
				route: AppRoutes.ResultsOperationalUnits,
				pageType: PageType.ResultsOperationalUnits,
			},
			/* { key: AppMenuKey.ResultsMap, route: AppRoutes.ResultsMap, pageType: PageType.ResultsMap }, */
		],
	},
	{
		key: AppMenuKey.WhatIf,
		route: AppRoutes.WhatIf,
		pageType: PageType.WhatIfSimulation,
		items: [
			{
				key: AppMenuKey.WhatIfSimulation,
				route: AppRoutes.WhatIfSimulation,
				pageType: PageType.WhatIfSimulation,
			},
			{
				key: AppMenuKey.WhatIfCarbonBudget,
				route: AppRoutes.WhatIfCarbonBudget,
				pageType: PageType.WhatIfCarbonBudget,
			},
			{
				key: AppMenuKey.WhatIfRootCause,
				route: AppRoutes.WhatIfRootCause,
				pageType: PageType.WhatIfRootCause,
			},
		],
	},
	{
		key: AppMenuKey.AdditionalServices,
		route: AppRoutes.AdditionalServices,
		pageType: PageType.AdditionalServicesDocumentation,
		items: [
			{
				key: AppMenuKey.AdditionalServicesDocumentation,
				route: AppRoutes.AdditionalServicesDocumentation,
				pageType: PageType.AdditionalServicesDocumentation,
			},
			{
				key: AppMenuKey.AdditionalServicesTourWizard,
				route: AppRoutes.AdditionalServicesTourWizard,
				pageType: PageType.AdditionalServicesTourWizard,
			},
			{
				key: AppMenuKey.AdditionalServicesCarbonSnap,
				route: AppRoutes.AdditionalServicesCarbonSnap,
				pageType: PageType.AdditionalServicesCarbonSnap,
			},
		],
	},
	{
		key: AppMenuKey.Settings,
		route: AppRoutes.Settings,
		pageType: PageType.SettingsAdmin,
		items: [
			{
				key: AppMenuKey.SettingsAdmin,
				route: AppRoutes.SettingsAdmin,
				pageType: PageType.SettingsAdmin,
			},
			{
				key: AppMenuKey.SettingsAuthApi,
				route: AppRoutes.SettingsAuthApi,
				pageType: PageType.SettingsAuthApi,
			},
			{
				key: AppMenuKey.SettingsPersonalised,
				route: AppRoutes.SettingsPersonalised,
				pageType: PageType.SettingsPersonalised,
			},
		],
	},
];
